import React, { Dispatch, SetStateAction, useState } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { MoreIcon } from '../../../components/icons/MoreIcon';
import { ConfirmDeleteBeneficiary } from './ConfirmDeleteBeneficiary';
import { Tag } from 'antd';
import { getBeneficiaryStatus, openNotification } from '../../../utils/helper';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    SyncOutlined
} from '@ant-design/icons';
import CustomTable from '../../../components/CustomTable';
import { Beneficiary } from '../../../interfaces/Beneficiary';
import UserBeneficiaryTableMobileCard from './UserBeneficiaryTableMobileCard';
import Button from '../../../components/button/Button';
import { hasPermission } from '../../auth';
import { useUserProfile } from '../../../customhooks/useUserProfile';
import {
    DeleteIcon,
    EditIcon,
    PlusIcon,
    RefreshCircleIcon
} from '../../../components/icons';
import { FilterParams, PagingParams } from '../../../interfaces/Filter';

interface BeneficiariesTableProps {
    isBeneficiariesLoading: boolean;
    beneficiaries: Beneficiary[];
    fetchBeneficiaries: () => void;
    isBeneficiariesFetching: boolean;
    setShowAddBeneficiary: (val: boolean) => void;
    setBeneficiaryId: (val: number) => void;
    beneficiariesFilterOptions: FilterParams;
    setBeneficiariesFilterOptions: Dispatch<SetStateAction<FilterParams>>;
    beneficiaryPaging: PagingParams;
}

const BeneficiariesTable = ({
    isBeneficiariesLoading,
    beneficiaries,
    fetchBeneficiaries,
    isBeneficiariesFetching,
    setShowAddBeneficiary,
    setBeneficiaryId,
    beneficiariesFilterOptions,
    setBeneficiariesFilterOptions,
    beneficiaryPaging
}: BeneficiariesTableProps) => {
    const { permissions } = useUserProfile();

    const [selected, setSelected] = useState<Beneficiary | null>(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    const handleEdit = (id: number) => {
        setBeneficiaryId(id);
        if (
            permissions &&
            hasPermission({
                userPermissions: permissions,
                module: 'payments',
                permissionList: ['edit', '*']
            })
        ) {
            setShowAddBeneficiary(true);
        } else {
            openNotification(
                'error',
                'You do not have access to perform this action'
            );
        }
    };

    const handleDelete = (item: Beneficiary) => {
        setSelected(item);
        setOpenDeleteModal(true);
    };

    const getMenu = (record: Beneficiary): MenuProps['items'] => [
        {
            key: '1',
            label: (
                <button
                    className='flex items-center h-[40px] w-[150px] gap-[16px] text-steelBlue text-base'
                    onClick={() => handleEdit(record.id)}
                >
                    <EditIcon />
                    <span>Edit</span>
                </button>
            )
        },
        {
            key: '2',
            label: (
                <button
                    className='flex items-center h-[40px] w-[150px] gap-[16px] text-steelBlue text-base'
                    onClick={() => handleDelete(record)}
                >
                    <DeleteIcon />
                    <span>Delete</span>
                </button>
            )
        }
    ];
    return (
        <div className='bg-white rounded-[8px] overflow-hidden'>
            <div className='flex justify-between items-center px-[24px] py-[20px]'>
                <h4 className='text-ebony font-semibold text-lg'>
                    Beneficiaries
                </h4>

                <div className='hidden lg:flex gap-2'>
                    <Button
                        leftIcon={<PlusIcon />}
                        text='Add Beneficiary'
                        variant='secondaryOutline'
                        onClick={() => setShowAddBeneficiary(true)}
                        size='sm'
                    />
                    <Button
                        leftIcon={<RefreshCircleIcon />}
                        text='Refresh'
                        variant='secondaryOutline'
                        onClick={fetchBeneficiaries}
                        size='sm'
                    />
                </div>

                <div className='lg:hidden  relative'>
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: '1',
                                    label: (
                                        <Button
                                            leftIcon={<PlusIcon />}
                                            text='Add Beneficiary'
                                            variant='tertiary'
                                            onClick={() =>
                                                setShowAddBeneficiary(true)
                                            }
                                        />
                                    )
                                },
                                {
                                    key: '2',
                                    label: (
                                        <Button
                                            leftIcon={<RefreshCircleIcon />}
                                            text='Refresh'
                                            variant='tertiary'
                                            onClick={fetchBeneficiaries}
                                        />
                                    )
                                }
                            ]
                        }}
                        trigger={['click']}
                        dropdownRender={(menu) => (
                            <div className='w-fit'>
                                {React.cloneElement(menu as React.ReactElement)}
                            </div>
                        )}
                    >
                        <button>
                            <MoreIcon />
                        </button>
                    </Dropdown>
                </div>
            </div>
            <CustomTable
                loadingData={isBeneficiariesLoading || isBeneficiariesFetching}
                data={beneficiaries}
                columns={[
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        render: (_, { status }) => (
                            <Tag
                                bordered={false}
                                color={
                                    getBeneficiaryStatus(status) === 'PENDING'
                                        ? 'processing'
                                        : getBeneficiaryStatus(status) ===
                                          'ENABLED'
                                        ? 'success'
                                        : 'error'
                                }
                                icon={
                                    getBeneficiaryStatus(status) ===
                                    'PENDING' ? (
                                        <SyncOutlined spin />
                                    ) : getBeneficiaryStatus(status) ===
                                      'ENABLED' ? (
                                        <CheckCircleOutlined />
                                    ) : (
                                        <CloseCircleOutlined />
                                    )
                                }
                            >
                                {getBeneficiaryStatus(status)}
                            </Tag>
                        )
                    },
                    {
                        title: 'Account Name',
                        dataIndex: 'beneficiaryCompanyName',
                        key: 'beneficiaryCompanyName'
                    },
                    {
                        title: 'Acc No',
                        dataIndex: 'accountNumber',
                        key: 'accountNumber'
                    },
                    {
                        title: 'Bank Name',
                        dataIndex: 'bankName',
                        key: 'bankName'
                    },
                    {
                        title: 'Currency',
                        dataIndex: 'currencyCode',
                        key: 'currencyCode'
                    },
                    {
                        title: 'Country',
                        dataIndex: 'beneficiaryCountryName',
                        key: 'beneficiaryCountryName'
                    },
                    {
                        title: 'Labels/Tags',
                        dataIndex: 'beneficiaryEntityType',
                        key: 'beneficiaryEntityType'
                    },
                    {
                        title: '',
                        render: (_, record) => (
                            <Dropdown
                                menu={{
                                    items: getMenu(record)
                                }}
                                trigger={['click']}
                            >
                                <div
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                >
                                    <MoreIcon />
                                </div>
                            </Dropdown>
                        )
                    }
                ]}
                mobileComponent={<UserBeneficiaryTableMobileCard />}
                showPagination
                paging={{
                    ...beneficiariesFilterOptions,
                    total: beneficiaryPaging.total
                }}
                updatePage={(value) => {
                    setBeneficiariesFilterOptions({
                        ...beneficiariesFilterOptions,
                        page: value
                    });
                }}
            />

            <ConfirmDeleteBeneficiary
                item={selected}
                open={openDeleteModal}
                onClose={() => setOpenDeleteModal(false)}
            />
        </div>
    );
};

export default BeneficiariesTable;
